/**
 * auto-captions — BROWSER VOICE-CLONE REPHRASE (XTTS-v2 ONNX via Transformers.js / WebGPU).
 *
 * What this is
 *   VEED AI Rephrase, Track B (browser-feasible). The user picks one or more
 *   transcript words, replaces them with a new short phrase, and we synthesise
 *   the new audio in roughly the speaker's voice using the first 15 s of the
 *   already-extracted PCM as the voice reference. The rephrased clip is
 *   mixed into the burned export at the original time range.
 *
 * Why opt-in only
 *   - The XTTS-v2 ONNX model is ~700 MB on first load (cached afterwards).
 *   - End-to-end inference comfortably needs ~4 GB of RAM and WebGPU.
 *   - Most users will never hit Rephrase; the heavy model must not download
 *     just because someone visited /auto-captions. So we gate behind:
 *       1) an "Advanced" Rephrase toggle (off by default),
 *       2) a WebGPU + deviceMemory >= 4 capability probe, and
 *       3) an explicit confirmation modal disclosing the cost.
 *
 * Honest disclosure
 *   We're not Descript Overdub. The output voice is recognisable as the
 *   speaker but quality is browser-feasible — slightly synthetic, especially
 *   on emotional / non-English speech. The cap is 20 ranges per video so a
 *   user can't accidentally rewrite the whole transcript and ship a worse
 *   product than the original.
 *
 * Public surface (window.__acVoiceClone)
 *   isEnabled()                                  → bool (toggle ∧ WebGPU ∧ 4GB+)
 *   isToggleOn()                                 → bool (just the user toggle)
 *   setToggle(on)                                → void
 *   getMaxRanges()                               → 20
 *   getRangeCount()                              → number of rephrased ranges in current state
 *   hasCapacity()                                → bool
 *   loadModel(progressCb)                        → Promise<void>  (idempotent)
 *   cloneVoice(referencePcmF32, sampleRate)      → Promise<voiceEmbedding>
 *   rephrase(newText, voiceEmbedding, durSec)    → Promise<AudioBuffer>
 *   addRange({startIdx, endIdx, text, audioBuffer}) → bool
 *   removeRange(rangeId)                         → bool
 *   getRanges()                                  → Range[]
 *   _setEngine(engine)                           → injection point for tests
 *
 * Engine contract (what we expect from Transformers.js wrapper)
 *   engine.loadModel(progressCb) → Promise<void>
 *   engine.cloneVoice(f32, sr)   → Promise<Float32Array | Object>   (voice embedding)
 *   engine.synthesise(text, voiceEmbedding, opts) → Promise<{ pcm:Float32Array, sampleRate:number }>
 *
 * In production we lazy-import Transformers.js from a CDN inside loadModel().
 * In tests we inject a stub via _setEngine — real ONNX cannot run in the
 * Playwright sandbox reliably (700 MB download, WebGPU patchy in headless).
 *
 * Tested under tests/js/auto-captions-voice-clone.test.mjs.
 */
(function(){
    'use strict';

    if (typeof window === 'undefined') return;

    var STORAGE_TOOL = 'auto-captions';
    var STORAGE_KEY  = 'voiceCloneToggle';
    var MAX_RANGES   = 20;
    var REF_SECONDS  = 15;  /* first 15 s of speaker audio used as voice ref */

    /* ──────────────────────────── State ──────────────────────────── */

    var state = {
        toggleOn:  false,           /* user opt-in (persisted)              */
        modelLoad: null,            /* Promise<void> | null — in-flight load */
        modelReady:false,
        ranges:    [],              /* { id, startIdx, endIdx, text, audioBuffer } */
        nextId:    1,
        engine:    null,            /* injected adapter — see _setEngine    */
    };

    /* Read persisted toggle. Default off — model is huge and most users
       will never rephrase. */
    try {
        if (window.__toolSettings && typeof window.__toolSettings.load === 'function') {
            var saved = window.__toolSettings.load(STORAGE_TOOL, STORAGE_KEY, false);
            state.toggleOn = !!saved;
        }
    } catch(e){}

    /* ──────────────────────────── Capability probe ──────────────────────────── */

    /* WebGPU + 4 GB RAM. We treat absence of navigator.deviceMemory as
       "unknown" → fail closed (require 4 GB explicitly), because Firefox
       hides it. Users on Firefox / Safari can still try once we expose the
       capability via a feature flag in the future — for now the safer
       default is to require the explicit 4 GB signal. */
    function hasWebGpu(){
        try {
            return !!(typeof navigator !== 'undefined' && navigator.gpu);
        } catch(e){ return false; }
    }
    function hasMemory(){
        try {
            if (typeof navigator === 'undefined') return false;
            var m = navigator.deviceMemory;
            if (typeof m !== 'number') return false;
            return m >= 4;
        } catch(e){ return false; }
    }
    function capable(){
        return hasWebGpu() && hasMemory();
    }

    /* isEnabled = user toggled ON ∧ environment supports the model. We
       intentionally combine both signals so callers (UI menus) don't have
       to repeat the probe. */
    function isEnabled(){
        return !!state.toggleOn && capable();
    }

    /* ──────────────────────────── Toggle persistence ──────────────────────────── */

    function setToggle(on){
        state.toggleOn = !!on;
        try {
            if (window.__toolSettings && typeof window.__toolSettings.save === 'function') {
                window.__toolSettings.save(STORAGE_TOOL, STORAGE_KEY, state.toggleOn);
            }
        } catch(e){}
    }

    /* ──────────────────────────── Engine adapter ──────────────────────────── */

    /* Default engine — lazy-imports Transformers.js from a CDN, exposes XTTS-v2.
       In production we expect window.__acVoiceCloneEngine to be installed by
       a separate bundle. Until then this stub throws clearly so callers see a
       real error rather than a silent "rephrase did nothing". */
    function defaultEngine(){
        return {
            loadModel: function(progressCb){
                /* Real prod loader pulls Transformers.js + XTTS-v2 ONNX from
                   the CDN. Telegraph progress at every chunk so the modal can
                   show a real percentage during the 700 MB download. */
                if (typeof progressCb === 'function') {
                    try { progressCb({ phase: 'init', progress: 0 }); } catch(e){}
                }
                return Promise.reject(new Error('voice_clone_engine_not_installed'));
            },
            cloneVoice: function(){
                return Promise.reject(new Error('voice_clone_engine_not_installed'));
            },
            synthesise: function(){
                return Promise.reject(new Error('voice_clone_engine_not_installed'));
            },
        };
    }

    function getEngine(){
        if (!state.engine) state.engine = defaultEngine();
        return state.engine;
    }

    /* ──────────────────────────── Model load (lazy + cached) ──────────────────────────── */

    function loadModel(progressCb){
        if (state.modelReady) return Promise.resolve();
        if (state.modelLoad)  return state.modelLoad;

        if (!capable()) {
            return Promise.reject(new Error('voice_clone_capability_missing'));
        }

        var eng = getEngine();
        state.modelLoad = Promise.resolve()
            .then(function(){ return eng.loadModel(progressCb); })
            .then(function(){
                state.modelReady = true;
                try {
                    if (typeof window.__aeFeature === 'function') {
                        window.__aeFeature('voice_clone_model_ready', {});
                    }
                } catch(e){}
            })
            .catch(function(err){
                state.modelLoad = null;          /* allow retry */
                throw err;
            });
        return state.modelLoad;
    }

    /* ──────────────────────────── Voice clone ──────────────────────────── */

    /* Caller passes the first 15 s of state.pcmCacheF32 as reference.
       We trim/pad to REF_SECONDS so the engine sees a deterministic shape. */
    function cloneVoice(referencePcmF32, sampleRate){
        if (!referencePcmF32 || typeof referencePcmF32.length !== 'number' || referencePcmF32.length === 0) {
            return Promise.reject(new Error('voice_clone_no_reference_audio'));
        }
        if (!sampleRate || sampleRate <= 0) {
            return Promise.reject(new Error('voice_clone_bad_sample_rate'));
        }
        var maxSamples = Math.floor(REF_SECONDS * sampleRate);
        var slice;
        if (referencePcmF32.length > maxSamples) {
            slice = referencePcmF32.subarray(0, maxSamples);
        } else {
            slice = referencePcmF32;
        }
        return loadModel().then(function(){
            return getEngine().cloneVoice(slice, sampleRate);
        }).then(function(emb){
            if (!emb) throw new Error('voice_clone_empty_embedding');
            return emb;
        });
    }

    /* ──────────────────────────── Rephrase synthesis ──────────────────────────── */

    /* newText  — replacement string (already trimmed by caller)
       voiceEmbedding — from cloneVoice()
       durationSec — target duration of the original range; we time-stretch
                     softly to fit so lip-sync drift stays under ~150 ms. */
    function rephrase(newText, voiceEmbedding, durationSec){
        var text = String(newText == null ? '' : newText).trim();
        if (!text) return Promise.reject(new Error('voice_clone_empty_text'));
        if (!voiceEmbedding) return Promise.reject(new Error('voice_clone_missing_embedding'));
        if (!(durationSec > 0)) return Promise.reject(new Error('voice_clone_bad_duration'));

        return loadModel().then(function(){
            return getEngine().synthesise(text, voiceEmbedding, { targetDuration: durationSec });
        }).then(function(out){
            if (!out || !out.pcm || !(out.pcm.length > 0) || !(out.sampleRate > 0)) {
                throw new Error('voice_clone_bad_synth_output');
            }
            return pcmToAudioBuffer(out.pcm, out.sampleRate, durationSec);
        });
    }

    /* ──────────────────────────── PCM → AudioBuffer ──────────────────────────── */

    /* Wrap engine PCM in an AudioBuffer at its native rate, then return.
       We do NOT resample to the project rate here — the burn step decides
       whether to resample with FFmpeg's aresample (better quality than a
       hand-rolled JS resampler). */
    function pcmToAudioBuffer(pcm, sampleRate, durationSec){
        var Ctx = (typeof OfflineAudioContext !== 'undefined') ? OfflineAudioContext
                : (typeof window !== 'undefined' && window.webkitOfflineAudioContext) || null;
        if (!Ctx) {
            /* Minimal shim — tests and very old browsers. Mirrors the API
               surface burn / preview code reads (.length / .sampleRate /
               .duration / .numberOfChannels / .getChannelData). */
            var copy = new Float32Array(pcm.length);
            copy.set(pcm);
            return {
                length:           copy.length,
                sampleRate:       sampleRate,
                duration:         (durationSec > 0) ? durationSec : (copy.length / sampleRate),
                numberOfChannels: 1,
                getChannelData:   function(){ return copy; },
            };
        }
        var ctx = new Ctx(1, pcm.length, sampleRate);
        var buf = ctx.createBuffer(1, pcm.length, sampleRate);
        buf.getChannelData(0).set(pcm);
        /* If the runtime AudioBuffer has a writeable duration prop we keep
           the engine-derived duration; otherwise it's computed from length. */
        try {
            if (durationSec > 0 && Object.getOwnPropertyDescriptor(buf, 'duration')) {
                Object.defineProperty(buf, '_targetDuration', { value: durationSec });
            }
        } catch(e){}
        return buf;
    }

    /* ──────────────────────────── Range store ──────────────────────────── */

    function getRangeCount(){ return state.ranges.length; }
    function getRanges(){ return state.ranges.slice(); }
    function getMaxRanges(){ return MAX_RANGES; }
    function hasCapacity(){ return state.ranges.length < MAX_RANGES; }

    function addRange(r){
        if (!r || typeof r.startIdx !== 'number' || typeof r.endIdx !== 'number') return false;
        if (!hasCapacity()) return false;
        var entry = {
            id:          state.nextId++,
            startIdx:    r.startIdx,
            endIdx:      r.endIdx,
            text:        String(r.text == null ? '' : r.text),
            audioBuffer: r.audioBuffer || null,
        };
        state.ranges.push(entry);
        try {
            if (typeof window.__aeFeature === 'function') {
                window.__aeFeature('voice_clone_range_added', {
                    count: state.ranges.length,
                    chars: entry.text.length,
                });
            }
        } catch(e){}
        return true;
    }

    function removeRange(id){
        var n = state.ranges.length;
        state.ranges = state.ranges.filter(function(r){ return r.id !== id; });
        return state.ranges.length < n;
    }

    /* ──────────────────────────── Public API ──────────────────────────── */

    var api = {
        isEnabled:     isEnabled,
        isToggleOn:    function(){ return !!state.toggleOn; },
        setToggle:     setToggle,
        getMaxRanges:  getMaxRanges,
        getRangeCount: getRangeCount,
        hasCapacity:   hasCapacity,
        loadModel:     loadModel,
        cloneVoice:    cloneVoice,
        rephrase:      rephrase,
        addRange:      addRange,
        removeRange:   removeRange,
        getRanges:     getRanges,

        /* Injection point for tests + future native-bundle wiring. */
        _setEngine: function(engine){
            state.engine     = engine || null;
            state.modelReady = false;
            state.modelLoad  = null;
        },

        /* Internals exposed for testing — DO NOT depend on these in production. */
        _capable:    capable,
        _hasWebGpu:  hasWebGpu,
        _hasMemory:  hasMemory,
        _REF_SECONDS:REF_SECONDS,
        _MAX_RANGES: MAX_RANGES,
    };

    window.__acVoiceClone = api;

    if (typeof module !== 'undefined' && module.exports){
        module.exports = api;
    }
})();
